// AthleteDashboard.js - Enhanced version with stats cards
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import styles from './AthleteDashboard.module.css';
import routes from 'routes';
import {
  getAthletesByMemberEmail,
  getEventHistory,
  getAthleteDetailsByAthleteId,
  getMemberDetailByAthleteID,
  exportAthletePbReportToFile,
} from 'api/userApi';
import { getActiveSeason, getSeasons } from 'api/season';
import { getSeasonBusinessRules } from 'services/admin/SeasonService';
import { exportAndDownloadFile } from 'utils/fileDownload';
import { historyResultDataColumns } from 'config/admin/athleteDashboard';
import {
  getUserPermissionLevel,
  getUserEmail,
} from 'services/auth/tokenService';
import {
  createEventHistory,
  generateAthleteOptions,
  //getHistoryPR,
  historyResultData,
} from 'services/admin/AthleteDashboardService';

import Loading from 'components/common/Loading';
import MetaTags from 'components/common/MetaTags';
import DashboardLayout from 'components/dashboard/DashboardLayout';
import AdminTab from 'components/admin/AdminTab';
import AdminDataTable from 'components/admin/AdminDataTable';
import ResultHistoryPopup from 'components/admin/ResultHistoryPopup';
import AthleteRecentMeet from 'components/admin/AthleteDashboard/AthleteRecentMeet';
import AthletePastMeet from 'components/admin/AthleteDashboard/AthletePastMeet';
import AthleteTrainingResults from 'components/admin/AthleteDashboard/AthleteTrainingResults';
import CustomToggle from 'components/NewDesign/CustomToggle';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import CustomNewButton from 'components/NewDesign/CustomNewButton';

//import { ReactComponent as StarIcon } from 'assets/images/icon_star.svg';
import ChartToolTip from './ChartToolTip';
import TextButton from 'components/common/TextButton';
import { getUserInfo } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';
import { formatPoints } from 'utils/points';

const AthleteDashboard = () => {
  const userPermissionLevel = getUserPermissionLevel();
  const userInfo = JSON.parse(getUserInfo());

  const { selectedClub } = useSelector((state) => state.user || {});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const athleteId = searchParams.get('athleteId');

  // Testing/support affordance, deliberately URL-only (no UI entry point): an
  // Official+ user can open this page as ?memberEmail=... to see it exactly as
  // that member does. The server independently enforces Official+ for another
  // member's data, so the param does nothing for regular members.
  const viewAsEmail = searchParams.get('memberEmail');
  const isViewingAsMember = Boolean(
    viewAsEmail && userPermissionLevel >= UserPermissionLevel.Official
  );
  const email = isViewingAsMember ? viewAsEmail : getUserEmail();

  const [athletes, setAthletes] = useState([]);

  const [fetchingAthletes, setFetchingAthletes] = useState(true);
  const [fetchingAthleteDetails, setFetchingAthleteDetails] = useState(true);
  const [fetchingEventHistory, setFetchingEventHistory] = useState(true);

  const [showPopup, setShowPopup] = useState(false);

  const [tabValue, setTabValue] = useState(athleteId || null);

  const [athleteDetail, setAthleteDetail] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonID, setSelectedSeasonID] = useState('');
  const [fallbackSeasonName, setFallbackSeasonName] = useState('');
  const [resultView, setResultView] = useState('competition');
  const [trainingStats, setTrainingStats] = useState(null);

  const [historyEventType, setHistoryEventType] = useState(null);
  const [historySeasonName, setHistorySeasonName] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [historyResults, setHistoryResults] = useState([]);
  const [historyPR, setHistoryPR] = useState('');

  const fetchAthletes = useCallback(async () => {
    try {
      const response = await getAthletesByMemberEmail(
        selectedClub?.clubID,
        email
      );
      return response.data;
    } catch (err) {
      console.log('[AthleteDashboard] Fetch athletes error: ', err);
      setAthletes([]);
      setFetchingAthleteDetails(false);
      return [];
    }
  }, [selectedClub?.clubID, email]);

  const fetchAthleteDetails = useCallback(async () => {
    try {
      setFetchingAthleteDetails(true);
      const response = await getAthleteDetailsByAthleteId(
        selectedClub?.clubID,
        tabValue,
        selectedSeasonID || null
      );
      return response.data;
    } catch (err) {
      console.log('[AthleteDashboard] Fetch recent result error: ', err);
      const serverMessage =
        err.response?.data?.message || err.response?.data?.Message;
      toast.error(serverMessage || 'Failed to load athlete results');
      return null;
    }
  }, [selectedClub?.clubID, tabValue, selectedSeasonID]);

  const fetchEventHistory = useCallback(async () => {
    try {
      setFetchingEventHistory(true);
      if (tabValue && selectedClub?.clubID && historyEventType) {
        const response = await getEventHistory(
          selectedClub?.clubID,
          tabValue,
          historyEventType,
          selectedSeasonID || null
        );
        return response.data;
      } else {
        return [];
      }
    } catch (err) {
      console.log('[AthleteDashboard] Fetch event history error: ', err);
      return [];
    }
  }, [selectedClub?.clubID, tabValue, historyEventType, selectedSeasonID]);

  const clickTab = useCallback(
    (value) => {
      setTabValue(value);
      // Preserve the view-as param across athlete tab switches
      const viewAsSuffix = viewAsEmail
        ? `&memberEmail=${encodeURIComponent(viewAsEmail)}`
        : '';
      navigate(
        `${routes.admin.athleteDashboard}?athleteId=${value}${viewAsSuffix}`
      );
    },
    [navigate, viewAsEmail]
  );

  const openHistoryPopup = (eventType) => {
    setShowPopup(true);
    setHistoryEventType(eventType);
  };

  const closeHistoryPopup = () => {
    setShowPopup(false);
  };

  /**
   * Formats Y-axis tick values based on the unit type from the chart data
   * @param {number} value - The numeric value to format
   * @param {Array} historyData - The chart data containing unit information
   * @returns {string} - Formatted value
   */
  const formatYAxisTick = (value, historyData) => {
    // Get the unit from the first data point (all points should have the same unit)
    const unit = historyData?.[0]?.unit;

    if (unit === 's') {
      // For time events, convert seconds back to time format if over 60
      if (value >= 60) {
        const minutes = Math.floor(value / 60);
        const seconds = (value % 60).toFixed(2);
        return `${minutes}:${seconds.padStart(5, '0')}`;
      } else {
        return value.toFixed(2);
      }
    } else {
      // For distance events (unit === 'm'), show consistent decimal places
      return value.toFixed(2);
    }
  };

  useEffect(() => {
    fetchAthletes().then((athletes) => {
      setAthletes(athletes);
      setFetchingAthletes(false);
    });
  }, [fetchAthletes]);

  const canBrowseSeasons = userPermissionLevel >= UserPermissionLevel.Member;

  useEffect(() => {
    if (!selectedClub?.clubID) return;
    if (!canBrowseSeasons) {
      // Guest-tier contacts can't list seasons (Member+ endpoint) — they stay on the
      // current season and only need its name for the header.
      getActiveSeason(selectedClub.clubID)
        .then((res) => setFallbackSeasonName(res.data?.name || ''))
        .catch((err) => {
          console.log('[AthleteDashboard] Fetch active season error: ', err);
          setFallbackSeasonName('');
        });
      return;
    }
    getSeasons(selectedClub.clubID)
      .then((res) => {
        const { activeSeason, sortedSeasons } = getSeasonBusinessRules(
          res.data || []
        );
        // The dashboard offers the current season plus the most recent previous
        // season only — older seasons predate data cleanup and are not exposed.
        const previousSeason = sortedSeasons.find(
          (s) => s.status !== 'Active' && s.status !== 'Draft'
        );
        setSeasons([activeSeason, previousSeason].filter(Boolean));
      })
      .catch((err) => {
        console.log('[AthleteDashboard] Fetch seasons error: ', err);
        setSeasons([]);
      });
  }, [selectedClub?.clubID, canBrowseSeasons]);

  useEffect(() => {
    if (tabValue) {
      fetchAthleteDetails().then((res) => {
        if (res) {
          setAthleteDetail(res);
          // Auto-set result view based on available data
          if (res.hasCompetitionResults) {
            setResultView('competition');
          } else if (res.hasTrainingResults) {
            setResultView('training');
          } else {
            setResultView('competition');
          }
        } else {
          // Never keep another season's data on screen after a failed fetch;
          // fall back to the current season so the page stays usable.
          setAthleteDetail(null);
          setSelectedSeasonID('');
        }
        setFetchingAthleteDetails(false);
      });
    }
  }, [fetchAthleteDetails, tabValue]);

  useEffect(() => {
    fetchEventHistory().then((eventHistory) => {
      setFetchingEventHistory(false);
      setHistorySeasonName(eventHistory.seasonName);
      if (eventHistory.results) {
        setHistoryData(createEventHistory(eventHistory.results));
        setHistoryResults(eventHistory.results);
        //setHistoryPR(getHistoryPR(eventHistory.results));
        const seasonPR = eventHistory.results?.[0]?.seasonPRValue || '';
        setHistoryPR(seasonPR);
      }
    });
  }, [fetchEventHistory]);

  useEffect(() => {
    if (athletes.length > 0 && !tabValue) {
      setTabValue(athletes[0].athleteID);
    }
  }, [athletes, tabValue]);

  const gotoMemberDetails = async () => {
    const res = await getMemberDetailByAthleteID(
      selectedClub?.clubID,
      athleteId
    );
    if (res.data) {
      navigate(
        routes.admin.members.showMember.url(
          selectedClub?.clubID,
          res.data.email
        )
      );
    }
  };

  // Season selection: '' = current season. The dropdown offers the current season
  // plus the most recent previous season only.
  const activeSeasonID =
    seasons.find((s) => s.isCurrent)?.seasonID || seasons[0]?.seasonID || '';
  const effectiveSeasonID = selectedSeasonID || activeSeasonID;
  const selectedSeason = seasons.find((s) => s.seasonID === effectiveSeasonID);
  const seasonName = selectedSeason?.name || fallbackSeasonName;
  const seasonOptions = seasons.map((s) => ({
    value: s.seasonID,
    label: s.isCurrent ? `${s.name} (Current)` : s.name,
  }));

  const handleSeasonChange = (name, value) => {
    setSelectedSeasonID(value === activeSeasonID ? '' : value);
  };

  const handleExportPbReport = async () => {
    await exportAndDownloadFile(
      exportAthletePbReportToFile,
      [selectedClub?.clubID, tabValue, selectedSeasonID || null],
      '',
      'The PB report was successfully exported',
      'Fetching PB report data from the server...'
    );
  };

  // Render competition stats cards
  const renderCompetitionStats = () => {
    if (!athleteDetail?.summaryStats) return null;

    const { summaryStats } = athleteDetail;
    // Points are only kept for the current season, so the card is hidden on a
    // previous-season view (the other cards are derived from results).
    const showPoints = athleteDetail.isCurrentSeason !== false;

    return (
      <div className={styles.statsGrid}>
        <div className={`${styles.statCard} ${styles.meets}`}>
          <span className={styles.statNumber}>
            {summaryStats.numberOfMeets}
          </span>
          <div className={styles.statLabel}>Meets</div>
        </div>

        <div className={`${styles.statCard} ${styles.events}`}>
          <span className={styles.statNumber}>
            {summaryStats.numberOfEvents}
          </span>
          <div className={styles.statLabel}>Events</div>
        </div>

        <div className={`${styles.statCard} ${styles.prs}`}>
          <span className={styles.statNumber}>{summaryStats.numberOfPRs}</span>
          <div className={styles.statLabel}>Season Bests</div>
        </div>

        {showPoints && (
          <div className={`${styles.statCard} ${styles.points}`}>
            <span className={styles.statNumber}>
              {formatPoints(summaryStats.seasonPointsExact ?? summaryStats.seasonPoints)}
            </span>
            <div className={styles.statLabel}>Season Points</div>
          </div>
        )}
      </div>
    );
  };

  // Render training stats cards
  const renderTrainingStats = () => {
    if (!trainingStats) return null;

    return (
      <div className={styles.statsGrid}>
        <div className={`${styles.statCard} ${styles.meets}`}>
          <span className={styles.statNumber}>
            {trainingStats.numberOfSessions}
          </span>
          <div className={styles.statLabel}>Sessions</div>
        </div>

        <div className={`${styles.statCard} ${styles.events}`}>
          <span className={styles.statNumber}>
            {trainingStats.numberOfEvents}
          </span>
          <div className={styles.statLabel}>Events</div>
        </div>

        <div className={`${styles.statCard} ${styles.prs}`}>
          <span className={styles.statNumber}>
            {trainingStats.numberOfRuns}
          </span>
          <div className={styles.statLabel}>Total Runs</div>
        </div>
      </div>
    );
  };


  return fetchingAthletes ? (
    <Loading isLoadingAdminPage={true} />
  ) : (
    <>
      <MetaTags title="SplitFast | My Athlete Results" />
      <DashboardLayout
        title="My Athlete Results"
        seasonName={seasonName}
        clubName={selectedClub?.clubName}
        userName={`${userInfo?.firstName} ${userInfo?.lastName}`}
      >
        <>
          {isViewingAsMember && (
            <div className={styles.viewAsBanner}>
              Viewing as <strong>{viewAsEmail}</strong>
            </div>
          )}
          <div className={styles.athleteTabSpacing}>
            <AdminTab
              options={generateAthleteOptions(athletes)}
              value={tabValue}
              clickTab={clickTab}
              disabled={fetchingAthleteDetails}
            />
          </div>
          {(seasonOptions.length > 1 || athleteDetail) && (
            <div className={styles.seasonRow}>
              {seasonOptions.length > 1 && (
                <div className={styles.seasonSelect}>
                  <CustomDropdown
                    label="Season"
                    value={effectiveSeasonID}
                    options={seasonOptions}
                    onChange={handleSeasonChange}
                    disabled={fetchingAthleteDetails}
                  />
                </div>
              )}
              <div className={styles.exportButtonWrap}>
                <CustomNewButton
                  variant="info"
                  onClick={handleExportPbReport}
                  label="Download PB Report"
                  disabled={
                    fetchingAthleteDetails ||
                    !athleteDetail?.hasCompetitionResults
                  }
                />
              </div>
            </div>
          )}
          {fetchingAthleteDetails ? (
            <Loading />
          ) : (
            athleteDetail && (
              <div style={{ position: 'relative' }}>
                {userPermissionLevel >= UserPermissionLevel.Official && (
                  <TextButton
                    style={{
                      position: 'absolute',
                      right: '0',
                      top: '10px',
                      color: 'black',
                      fontSize: '16px',
                    }}
                    onClick={gotoMemberDetails}
                    text="Member Details"
                  />
                )}

                {/* Stats cards - switch between competition and training */}
                {resultView === 'competition'
                  ? renderCompetitionStats()
                  : renderTrainingStats()}

                {/* Add spacing between stats and tables */}
                <div style={{ marginTop: '32px' }}>
                  {/* Toggle between Competition and Training */}
                  {athleteDetail.hasCompetitionResults &&
                    athleteDetail.hasTrainingResults && (
                      <div style={{ display: 'flex', marginBottom: '20px' }}>
                        <CustomToggle
                          items={[
                            { key: 'competition', label: 'Competition' },
                            { key: 'training', label: 'Training' },
                          ]}
                          selectedItem={resultView}
                          handleToggle={setResultView}
                        />
                      </div>
                    )}

                  {/* Competition view */}
                  <div
                    style={{
                      display:
                        resultView === 'competition' ? 'block' : 'none',
                    }}
                  >
                    {athleteDetail.recentMeet?.results?.length > 0 ||
                    athleteDetail.pastMeets?.length > 0 ? (
                      <>
                        <AthleteRecentMeet
                          recentMeet={athleteDetail.recentMeet}
                          clubId={selectedClub?.clubID}
                          openPopup={openHistoryPopup}
                        />
                        <AthletePastMeet
                          pastMeets={athleteDetail.pastMeets}
                          seasonName={athleteDetail.seasonName}
                          openPopup={openHistoryPopup}
                          clubId={selectedClub?.clubID}
                          isCurrentSeason={athleteDetail.isCurrentSeason !== false}
                        />
                      </>
                    ) : (
                      <>No competition results found</>
                    )}
                  </div>

                  {/* Training view - always mounted when training results exist, hidden via CSS */}
                  {athleteDetail.hasTrainingResults && (
                    <div
                      style={{
                        display:
                          resultView === 'training' ? 'block' : 'none',
                      }}
                    >
                      <AthleteTrainingResults
                        clubId={selectedClub?.clubID}
                        athleteId={tabValue}
                        seasonId={selectedSeasonID || null}
                        onStatsLoaded={setTrainingStats}
                      />
                    </div>
                  )}
                </div>
              </div>
            )
          )}
          {athletes?.length === 0 && <>No Athletes found</>}
        </>
      </DashboardLayout>

      <ResultHistoryPopup
        showPopup={showPopup}
        title={`${historyEventType} Event History`}
        subtitle={`${historySeasonName} season`}
        onClose={closeHistoryPopup}
        loading={fetchingEventHistory}
      >
        <h3
          className="heading1"
          style={{
            marginTop: 0,
            fontWeight: 600,
            color: 'var(--new-title-color)',
          }}
        >
          {athleteDetail?.firstName} {athleteDetail?.lastName}
        </h3>
        {historyPR && (
          <div className={styles.historyPR}>
            Season Best {historyPR}
            {/* <StarIcon style={{ width: '28px', height: '28px', marginLeft: '2px', color: '#ffc107' }} title="Season Best" /> */}
          </div>
        )}

        <ResponsiveContainer
          width="100%"
          height={300}
          style={{ margin: 'auto' }}
        >
          <AreaChart
            width={400}
            height={200}
            data={historyData}
            margin={{
              top: 40,
              right: 0,
              left: 0,
              bottom: 40,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis
              tickFormatter={(value) => formatYAxisTick(value, historyData)}
            />
            <Tooltip content={<ChartToolTip />} />
            <Area
              type="monotone"
              dataKey="record"
              stroke="#cf2c47"
              fill="#cf2c47"
              dot={true}
              activeDot={true}
            />
          </AreaChart>
        </ResponsiveContainer>
        {historyResults.length > 0 ? (
          <AdminDataTable
            borderType="full"
            isAddedFeatures={false}
            columns={historyResultDataColumns}
            data={historyResultData(historyResults)}
            searchInputPlaceholder=""
            headStyle={{
              gridTemplateColumns: 'repeat(4, 1fr)',
              background: '#f1f1f2',
            }}
            rowStyle={{
              gridTemplateColumns: 'repeat(4, 1fr)',
            }}
          />
        ) : (
          <>No history found</>
        )}
      </ResultHistoryPopup>
    </>
  );
};

export default AthleteDashboard;
